import { useState } from 'react';
import { Tooltip } from '../display/Tooltip';
import { getInitials } from '@airclou/utils';

interface AppItem {
  id: string;
  name: string;
  icon?: string;
  slug?: string;
  status?: 'active' | 'inactive' | 'suspended';
}

interface AppsStackProps {
  apps: AppItem[];
  maxVisible?: number;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  showTooltip?: boolean;
  variant?: 'default' | 'colored';
}

export function AppsStack({ 
  apps, 
  maxVisible = 3, 
  size = 'md',
  className = '',
  showTooltip = true,
  variant = 'default'
}: AppsStackProps) {
  const visibleApps = apps.slice(0, maxVisible);
  const remainingCount = Math.max(0, apps.length - maxVisible);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-8 h-8 text-sm',
    lg: 'w-10 h-10 text-base'
  };

  const getTooltipContent = (app: AppItem) => {
    const parts = [app.name];
    if (app.status) {
      parts.push(`Status: ${app.status}`);
    }
    return parts.join(' • ');
  };

  const getAppClasses = () => {
    const baseClasses = `relative ${sizeClasses[size]} rounded-lg border border-white dark:border-gray-800 flex items-center justify-center font-medium shadow-sm cursor-default`;
    
    if (variant === 'colored') {
      return `${baseClasses} bg-primary text-primary-foreground`;
    }
    
    return `${baseClasses} bg-primary text-primary-foreground`;
  };

  // Format large numbers for better display in small rectangles
  const formatOverflowCount = (count: number) => {
    if (count >= 1000) {
      return `+${Math.floor(count / 1000)}k`;
    }
    if (count >= 100) {
      return `+${count}`;
    }
    return `+${count}`;
  };

  // Get appropriate font size for overflow count
  const getOverflowFontSize = (count: number) => {
    const baseSizeClasses = {
      xs: count >= 100 ? 'text-[7px]' : 'text-[8px]',
      sm: count >= 100 ? 'text-[7px]' : 'text-[9px]',
      md: count >= 100 ? 'text-[8px]' : 'text-[10px]',
      lg: count >= 100 ? 'text-[10px]' : 'text-xs'
    };
    return baseSizeClasses[size];
  };

  const getOverflowClassesWithCount = (count: number) => {
    const baseClasses = `relative ${sizeClasses[size]} rounded-lg border border-white dark:border-gray-800 flex items-center justify-center font-medium shadow-sm cursor-default ${getOverflowFontSize(count)}`;
    return `${baseClasses} bg-gray-400 dark:bg-gray-500 text-white`;
  };

  return (
    <div className={`flex items-center -space-x-2 ${className}`}>
      {visibleApps.map((app, index) => (
        <Tooltip
          key={app.id}
          content={showTooltip ? getTooltipContent(app) : ''}
          show={showTooltip && hoveredIndex === index}
          position="top"
          delay={300}
          disabled={!showTooltip}
        >
          <div
            className={getAppClasses()}
            style={{ zIndex: maxVisible - index }}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            {app.icon ? (
              <img
                src={app.icon}
                alt={app.name}
                className="w-full h-full rounded-lg object-cover"
              />
            ) : (
              <span>{getInitials(app.name)}</span>
            )}
          </div>
        </Tooltip>
      ))}
      {remainingCount > 0 && (
        <Tooltip
          content={showTooltip ? `${remainingCount} more app${remainingCount > 1 ? 's' : ''}` : ''}
          show={showTooltip && hoveredIndex === visibleApps.length}
          position="top"
          delay={300}
          disabled={!showTooltip}
        >
          <div
            className={getOverflowClassesWithCount(remainingCount)}
            style={{ zIndex: 0 }}
            onMouseEnter={() => setHoveredIndex(visibleApps.length)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            {formatOverflowCount(remainingCount)}
          </div>
        </Tooltip>
      )}
    </div>
  );
}
